import { Injectable } from '@angular/core';

declare global {
    interface Window {
        zE?: (...args: any[]) => void;
    }
}

@Injectable({ providedIn: 'root' })
export class ZendeskWidgetService {
    private readonly queuedCalls: any[][] = [];
    private readyPromise: Promise<void> | null = null;
    private readyResolve: (() => void) | null = null;
    private readyWatcherStarted = false;
    private defaultsApplied = false;

    /**
     * Zendesk's widget script defines a global `window.zE(...)` function.
     * We proxy it so callers can safely invoke before the script is ready.
     */
    public zE(...args: any[]): void {
        if (typeof window !== 'undefined' && typeof window.zE === 'function') {
            window.zE(...args);
            return;
        }

        this.queuedCalls.push(args);
        void this.ready().then(() => this.flushQueue());
    }

    /**
     * Resolves once `window.zE` is available.
     */
    public ready(): Promise<void> {
        if (typeof window !== 'undefined' && typeof window.zE === 'function') {
            return Promise.resolve();
        }

        if (this.readyPromise) {
            return this.readyPromise;
        }

        this.readyPromise = new Promise<void>((resolve) => {
            this.readyResolve = resolve;
        });

        this.startReadyWatcher();
        return this.readyPromise;
    }

    /**
     * Applies the defaults previously configured via `NgxZendeskWebwidgetConfig.callback`.
     */
    public initClassicDefaults(locale = 'en'): void {
        if (this.defaultsApplied) {
            return;
        }
        this.defaultsApplied = true;

        this.zE('webWidget', 'setLocale', locale);
        this.zE('webWidget:on', 'close', () => {
            this.zE('webWidget', 'hide');
        });
    }

    /**
     * Backwards-compatible helper (was provided by `ngx-zendesk-webwidget`).
     */
    public async initZendesk(locale = 'en'): Promise<void> {
        await this.ready();
        this.initClassicDefaults(locale);
    }

    private startReadyWatcher(): void {
        if (this.readyWatcherStarted) {
            return;
        }
        this.readyWatcherStarted = true;

        const intervalMs = 50;
        const maxWaitMs = 15000;
        const start = Date.now();

        const timer = setInterval(() => {
            if (typeof window !== 'undefined' && typeof window.zE === 'function') {
                clearInterval(timer);
                this.flushQueue();
                this.readyResolve?.();
                this.readyResolve = null;
                return;
            }

            if (Date.now() - start > maxWaitMs) {
                clearInterval(timer);
                // If the snippet isn't present, we simply keep the queue; callers should degrade gracefully.
                this.readyResolve?.();
                this.readyResolve = null;
            }
        }, intervalMs);
    }

    private flushQueue(): void {
        if (typeof window === 'undefined' || typeof window.zE !== 'function') {
            return;
        }

        while (this.queuedCalls.length > 0) {
            const args = this.queuedCalls.shift();
            if (args) {
                window.zE(...args);
            }
        }
    }
}
